import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Logo } from "../components/Logo";
import { ProductsTab } from "../components/admin/ProductsTab";
import { OrdersTab } from "../components/admin/OrdersTab";
import { SettingsTab } from "../components/admin/SettingsTab";
import { DiscountsTab } from "../components/admin/DiscountsTab";
import { SalesCharts } from "../components/admin/SalesCharts";
import { AccountTab } from "../components/admin/AccountTab";
import { ReviewsTab } from "../components/admin/ReviewsTab";
import { DeliveryTab } from "../components/admin/DeliveryTab";
import { useAuth } from "../context/AuthContext";
import { api, money } from "../lib/api";

const Stat = ({ label, value, testId }) => (
  <div className="border border-white/10 bg-[#0f0f16] p-5">
    <p className="font-mono text-[11px] uppercase tracking-widest text-slate-400">{label}</p>
    <p className="mt-2 font-mono text-3xl font-bold text-white" data-testid={testId}>{value}</p>
  </div>
);

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const loadStats = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => null);
  }, []);

  useEffect(() => {
    if (user) loadStats();
  }, [user, loadStats]);

  if (user === null) return <div className="min-h-screen bg-[#07070b]" />;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#07070b] text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5">
          <Link to="/" data-testid="admin-store-link"><Logo /></Link>
          <button onClick={logout} className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-slate-300 hover:text-white" data-testid="admin-logout-button">
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-5 py-10">
        <h1 className="font-display text-6xl uppercase">Control room</h1>
        <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
          <Stat label="Revenue (paid)" value={money(stats?.revenue)} testId="stat-revenue" />
          <Stat label="Orders" value={stats?.orders ?? 0} testId="stat-orders" />
          <Stat label="Awaiting payment" value={stats?.pending ?? 0} testId="stat-pending" />
          <Stat label="Products" value={stats?.products ?? 0} testId="stat-products" />
        </div>
        <SalesCharts stats={stats} />
        <Tabs defaultValue="orders" className="mt-10">
          <TabsList className="h-auto flex-wrap rounded-none border border-white/10 bg-[#0f0f16]">
            <TabsTrigger value="orders" data-testid="admin-tab-orders" className="rounded-none font-mono text-xs uppercase tracking-widest data-[state=active]:bg-[#a259ff] data-[state=active]:text-black">Orders</TabsTrigger>
            <TabsTrigger value="products" data-testid="admin-tab-products" className="rounded-none font-mono text-xs uppercase tracking-widest data-[state=active]:bg-[#a259ff] data-[state=active]:text-black">Accounts</TabsTrigger>
            <TabsTrigger value="discounts" data-testid="admin-tab-discounts" className="rounded-none font-mono text-xs uppercase tracking-widest data-[state=active]:bg-[#a259ff] data-[state=active]:text-black">Promo codes</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="admin-tab-reviews" className="rounded-none font-mono text-xs uppercase tracking-widest data-[state=active]:bg-[#a259ff] data-[state=active]:text-black">Reviews</TabsTrigger>
            <TabsTrigger value="delivery" data-testid="admin-tab-delivery" className="rounded-none font-mono text-xs uppercase tracking-widest data-[state=active]:bg-[#a259ff] data-[state=active]:text-black">Delivery time</TabsTrigger>
            <TabsTrigger value="settings" data-testid="admin-tab-settings" className="rounded-none font-mono text-xs uppercase tracking-widest data-[state=active]:bg-[#a259ff] data-[state=active]:text-black">Payment info</TabsTrigger>
            <TabsTrigger value="account" data-testid="admin-tab-account" className="rounded-none font-mono text-xs uppercase tracking-widest data-[state=active]:bg-[#a259ff] data-[state=active]:text-black">Owner login</TabsTrigger>
          </TabsList>
          <TabsContent value="orders"><OrdersTab onChange={loadStats} /></TabsContent>
          <TabsContent value="products"><ProductsTab onChange={loadStats} /></TabsContent>
          <TabsContent value="discounts"><DiscountsTab codeStats={stats?.by_code} /></TabsContent>
          <TabsContent value="reviews"><ReviewsTab /></TabsContent>
          <TabsContent value="delivery"><DeliveryTab /></TabsContent>
          <TabsContent value="settings"><SettingsTab /></TabsContent>
          <TabsContent value="account"><AccountTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
